/**
 * Protected Route Component
 * Redirects to login if not authenticated
 * Supports role-based access control via requiredRole prop
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
`;

const ProtectedRoute = ({ children, adminOnly = false, requiredRole = null }) => {
  const { isAuthenticated, isAdmin, user, loading } = useAuth();

  if (loading) {
    return (
      <LoadingContainer>
        Loading...
      </LoadingContainer>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check admin-only access
  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Check required role access
  if (requiredRole) {
    const userRole = user?.role;
    
    // Admin has access to everything
    if (isAdmin) {
      return children;
    }

    // Check if user has the required role
    if (userRole !== requiredRole) {
      return <Navigate to="/" replace />;
    }
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  adminOnly: PropTypes.bool,
  requiredRole: PropTypes.oneOf(['user', 'student', 'admin'])
};

export default ProtectedRoute;
